import React, { useEffect, useState } from 'react';
import Pagination from "react-js-pagination";
import LoadingWheel from '../loadingwheel';
import SensorService from '../../services/sensorService';
import moment from 'moment';
import authStore from '../../store/authStore';
import { formatMlAlertLocalTime, toEpochMs } from '../MlAlertChartModal';
import { useHistory } from "react-router-dom";
import Routes from '../../routing/routes';
import { Modal } from 'react-bootstrap';
import Permissions from '../../config/permssions';

const maxLimit = 16;

export const flattenComponents = (components: any[]): Record<string, any> => {
  const result: Record<string, any> = {};
  (components || []).forEach((comp: any) => {
    const compType = comp?.comp_type;
    const seq = comp?.seq;
    if (!compType) return;

    Object.entries(comp).forEach(([key, value]) => {
      if (key !== 'comp_type' && key !== 'seq' && value != null) {
        if (seq) {
          result[`${compType}_${seq}.${key}`] = value;
        } else {
          result[`${compType}.${key}`] = value;
        }
      }
    });
  });
  return result;
};

const formatConfidence = (confidence: any): string => {
  if (confidence == null || confidence === '') return '-';
  const value = Number(confidence);
  if (Number.isNaN(value)) return '-';
  const pct = value <= 1 ? value * 100 : value;
  return `${pct.toFixed(2)}%`;
};

const formatSeverity = (item: any): string => {
  const severity = item?.ml?.severity;
  if (severity) return String(severity).toUpperCase();

  switch (item?.trigger_code?.color) {
    case 'red':
    case 'darkred':
      return 'HIGH';
    case 'orange':
      return 'MEDIUM';
    case 'yellow':
      return 'LOW';
    default:
      return '-';
  }
};

const getDetectedOnMs = (item: any): number | null => {
  const chart = item?.ml?.chart || {};
  const endMs = toEpochMs(chart.end_ms);
  if (endMs != null) return endMs;

  const points = chart.points;
  if (Array.isArray(points) && points.length) {
    const lastPoint = points[points.length - 1];
    const lastT = toEpochMs(lastPoint?.t ?? lastPoint?.timestamp ?? lastPoint?.time);
    if (lastT != null) return lastT;
  }

  return toEpochMs(chart.detected_at_ms);
};

const formatDetectedOn = (item: any): string => {
  const detectedAt = getDetectedOnMs(item);
  if (detectedAt == null) return '';
  return formatMlAlertLocalTime(detectedAt, 'MM/DD/YYYY, HH:mm:ss');
};

interface Props {
  assetId?: string;
  sensorBox: any;
  onShowChart?: (alertLog: any) => void;
  /** Use sensorbox issue-tracker params (`system_id`) instead of asset (`group_id`) */
  useSensorboxIssueTracker?: boolean;
}

const MlAlertLogsTab: React.FC<Props> = ({
  assetId,
  sensorBox,
  onShowChart,
  useSensorboxIssueTracker = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<Array<any>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [existingTicketModal, setExistingTicketModal] = useState(false);
  const [existingTicket, setExistingTicket] = useState<any>(null);
  const [currentAlertLog, setCurrentAlertLog] = useState<any>(null);
  const history = useHistory();

  const canViewDetail = authStore?.isOrgAdmin();
  const canCreateEditTickets = authStore?.currentUser?.access?.includes(Permissions.create_edit_issue_tracker);

  useEffect(() => {
    getMlLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, sensorBox]);

  const getMlLogs = async (pageNumber: number = 0) => {
    if (!assetId || sensorBox?.category === "generator") {
      setLogs([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    pageNumber = pageNumber === 0 ? currentPage : pageNumber;

    try {
      const response = await SensorService.getAssetMlLogs(assetId, pageNumber, maxLimit);
      setLogs(response.data.logs || []);
      setCurrentPage(pageNumber);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching ML alert logs:', error);
      setLogs([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const checkExistingTicket = async (alertLog: any) => {
    const code = alertLog.trigger_code?.code || '';
    const id = useSensorboxIssueTracker
      ? (sensorBox?._id || '')
      : (assetId || '');
    try {
      return await SensorService.checkExistingTicket(id, code);
    } catch (error) {
      console.error('Error checking existing ticket:', error);
      return { exists: false };
    }
  };

  const showIssueCreationPage = (alertLog: any) => {
    const orgid = sensorBox.org_id;
    let params = useSensorboxIssueTracker
      ? `?system_id=${sensorBox?._id}&status=${sensorBox?.status}`
      : `?group_id=${assetId}&status=${sensorBox?.status}`;

    if (authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)) {
      params += `&org_id=${orgid}`;
    }

    const triggerCode = alertLog.trigger_code || {};
    const desc = triggerCode.description || '';
    const color = triggerCode.color || '';
    const code = (triggerCode.code || '').replace(/#.*$/, '');

    params += `&alert_description=${desc}`;
    params += `&alert_code=${code}`;
    params += `&alert_color=${color}`;
    if (alertLog._id) {
      params += `&alert_log_id=${alertLog._id}`;
    }

    history.push({
      pathname: Routes.issuetrackerdetails,
      search: params,
    });
  };

  const handleIssueTrackerClick = async (alertLog: any) => {
    const existingTicketData = await checkExistingTicket(alertLog);
    if (existingTicketData.exists) {
      setExistingTicket(existingTicketData.ticket);
      setCurrentAlertLog(alertLog);
      setExistingTicketModal(true);
    } else {
      showIssueCreationPage(alertLog);
    }
  };

  const handleRowClick = (item: any) => {
    if (!canViewDetail) return;
    onShowChart?.(item);
  };

  return (
    <div>
      <div className="table-wrap">
        {loading ? (
          <LoadingWheel />
        ) : (
          <table
            className={logs.length > 0 ? "table custom-table table-style-logs text-custom-size" : "table custom-table table-style-no-logs "}
            style={{ tableLayout: 'fixed' }}
          >
            {logs.length > 0 ? (
              <>
                <thead className="table-header-bg">
                  <tr>
                    <th style={{ width: '3%' }} scope="col">#</th>
                    <th style={{ width: '10%' }} className="text-center" scope="col">Severity</th>
                    <th style={{ width: '40%' }} className="text-center" scope="col">Failure Mode</th>
                    <th style={{ width: '14%' }} className="text-center" scope="col">Confidence Score</th>
                    <th style={{ width: '22%' }} className="text-center" scope="col">Detected On</th>
                    {canCreateEditTickets && (
                      <th style={{ width: '11%' }} className="text-center" scope="col">Tickets</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((item: any, index) => {
                    const triggerCode = item.trigger_code || {};
                    const desc = triggerCode.description || '';
                    const color = triggerCode.color;
                    const severityLabel = formatSeverity(item);
                    const confidence = formatConfidence(triggerCode.confidence);
                    const detectedOn = formatDetectedOn(item);

                    return (
                      <tr
                        className={
                          `${index % 2 !== 0 ? "background-color" : ""} ${canViewDetail ? "click-icon" : ""}`.trim()
                        }
                        key={item._id || index}
                        onClick={() => handleRowClick(item)}
                      >
                        <td>{(maxLimit * (currentPage - 1)) + index + 1}</td>
                        <td className="text-center" title={severityLabel}>
                          <i className={`fa fa-circle ${color}-status`} aria-hidden="true"></i>
                          <span className="pl-1">{severityLabel}</span>
                        </td>
                        <td className="text-center" title={desc}>{desc}</td>
                        <td className="text-center" title={confidence}>{confidence}</td>
                        <td className="text-truncate text-center" title={detectedOn}>{detectedOn}</td>
                        {canCreateEditTickets && (
                          <td className="text-center">
                            <i
                              className="fa fa-flag cursor-pointer px-3"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                handleIssueTrackerClick(item);
                              }}
                              aria-hidden="true"
                            ></i>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </>
            ) : (
              <div>
                <p className='no-logs-text'>No logs found</p>
              </div>
            )}
          </table>
        )}
      </div>
      {logs.length > 0 && !loading && (
        <div className={Math.ceil(total / maxLimit) > 1 ? "pagination-container-logs" : "pagination-container-no-logs"}>
          {Math.ceil(total / maxLimit) > 1 ? (
            <Pagination
              hideNavigation={true}
              activePage={currentPage}
              itemsCountPerPage={maxLimit}
              totalItemsCount={total}
              itemClass={"item-class"}
              activeClass={"active-class"}
              pageRangeDisplayed={5}
              onChange={(pageNumber) => getMlLogs(pageNumber)}
            />
          ) : null}
        </div>
      )}

      <ExistingTicketModal
        show={existingTicketModal}
        onClose={() => setExistingTicketModal(false)}
        ticket={existingTicket}
        onOpenTicket={() => {
          if (existingTicket) {
            let params = `?ticket_id=${existingTicket.id}`;
            if (authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)) {
              params += `&org_id=${sensorBox.org_id}`;
            }
            history.push({
              pathname: Routes.issuetrackerdetails,
              search: params,
            });
          }
          setExistingTicketModal(false);
        }}
        onOpenNewTicket={() => {
          if (currentAlertLog) {
            showIssueCreationPage(currentAlertLog);
            setExistingTicketModal(false);
          }
        }}
      />

    </div>
  );
};

interface ExistingTicketModalProps {
  show: boolean;
  onClose: () => void;
  ticket: any;
  onOpenTicket: () => void;
  onOpenNewTicket: () => void;
}

const ExistingTicketModal: React.FC<ExistingTicketModalProps> = ({
  show,
  onClose,
  ticket,
  onOpenTicket,
  onOpenNewTicket,
}) => (
  <Modal show={show} onHide={onClose} size="sm" centered>
    <Modal.Header closeButton>
      <Modal.Title>Ticket: {ticket?.issue_id}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="mb-3">
        <strong>Title:</strong> {ticket?.title}
      </div>
      <div className="mb-3">
        <strong>Status:</strong>{" "}
        <span className={`badge badge-${ticket?.status === 'OPEN' ? 'warning' : ticket?.status === 'RESOLVED' ? 'success' : 'secondary'}`}>
          {ticket?.status}
        </span>
      </div>
      <div className="mb-3">
        <strong>Last Updated:</strong>{" "}
        {moment(ticket?.updated_on || ticket?.created_on).format('MM/DD/YYYY - HH:mm:ss')}
      </div>
    </Modal.Body>
    <Modal.Footer>
      <button className="btn btn-secondary" onClick={onOpenNewTicket}>
        Open New Ticket
      </button>
      <button className="btn btn-primary" onClick={onOpenTicket}>
        View Ticket
      </button>
    </Modal.Footer>
  </Modal>
);

export default MlAlertLogsTab;
